// _forms/_shared/hooks/useFormulario.js
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDataFetcher } from './useDataFetcher';
import { useFieldHandlers } from './useFieldHandlers';
import { useFormSaveHandler } from './useFormSaveHandler';
import { validators } from '../utils/validators';

export const useFormulario = ({
  slug,
  baseUrl,
  selectedPackage = null,
  isEditable = false,
  isFillingForm = false,
  onTabChange,
}) => {
  // ==========================================
  // 1. HOOKS EXTERNOS
  // ==========================================
  const {
    formConfig,
    setFormConfig,
    paquetes,
    selectedPaquete,
    title,
    setTitle,
    amount,
    setAmount,
    error: fetchError,
    isLoading,
    setSelectedPaquete,
  } = useDataFetcher(slug, baseUrl, selectedPackage);

  const {
    handleTypeChangeWrapper,
    handleAddCategoryRuleWrapper,
    handleUpdateCategoryRuleWrapper,
    handleDeleteCategoryRuleWrapper,
    handleToggleCategoryRulePackageWrapper,
    handleUpdateCategoryRuleConditionsWrapper,
    deletedFields,
    setDeletedFields,
    handleFieldChangeWrapper,
    handleLabelChangeWrapper,
    handleAddFieldWrapper,
    handleDeleteFieldWrapper,
    handleAddOptionWrapper,
    handleDeleteOptionWrapper,
    handleOptionChangeWrapper,
    handleOptionConditionsWrapper,
    handleRequiredToggleWrapper,
  } = useFieldHandlers([]);

  const { handleSave, error: saveError } = useFormSaveHandler(
    baseUrl,
    slug,
    formConfig?.fields?.fields || [],
    paquetes,
    deletedFields,
    selectedPaquete,
    title,
    amount
  );

  // ==========================================
  // 2. ESTADOS LOCALES
  // ==========================================
  const [selectedAddons, setSelectedAddons] = useState({});
  const [aceptaDeslinde, setAceptaDeslinde] = useState(false);
  const [resumenModal, setResumenModal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [existingConditions, setExistingConditions] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [optionConditionModal, setOptionConditionModal] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const lastProcessedDate = useRef(null);
  const lastProcessedPackage = useRef(null);
  const lastProcessedRulesSignature = useRef(null);

  // ==========================================
  // 3. FUNCIONES UTILITARIAS
  // ==========================================
  const ageCalculationMethod = formConfig?.ageCalculationMethod || 'current';
  const calculateAge = useCallback((birthDateString) => {
    if (!birthDateString) return null;
    const birthDate = new Date(birthDateString);
    if (isNaN(birthDate.getTime())) return null;

    let referenceDate;
    if (ageCalculationMethod === 'endOfYear') {
      const currentYear = new Date().getFullYear();
      referenceDate = new Date(currentYear, 11, 31);
    } else {
      referenceDate = new Date();
    }

    let age = referenceDate.getFullYear() - birthDate.getFullYear();
    const m = referenceDate.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && referenceDate.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? age : 0;
  }, [ageCalculationMethod]);

  // ==========================================
  // 4. CÁLCULOS DERIVADOS (useMemo)
  // ==========================================
  const totalAmount = useMemo(() => {
    const paquete = paquetes.find((p) => 
      String(p.id_package) === String(selectedPaquete) || 
      String(p.id) === String(selectedPaquete)
    );
    if (!paquete) return '0.00';
    const base = (parseFloat(paquete.amount) || 0) * (parseInt(paquete.number_of_competitors) || 1);
    const extra = (paquete.addons || []).reduce((sum, a) => {
      return sum + (parseFloat(a.extra_amount) || 0) * (parseInt(selectedAddons[a.id]) || 0);
    }, 0);
    return (base + extra).toFixed(2);
  }, [paquetes, selectedPaquete, selectedAddons]);

  const amountPerPerson = useMemo(() => {
    const paquete = paquetes.find((p) => 
      String(p.id_package) === String(selectedPaquete) || 
      String(p.id) === String(selectedPaquete)
    );
    return paquete?.amount || '0';
  }, [paquetes, selectedPaquete]);

  const numberOfCompetitors = useMemo(() => {
    const paquete = paquetes.find((p) => 
      String(p.id_package) === String(selectedPaquete) || 
      String(p.id) === String(selectedPaquete)
    );
    return paquete?.number_of_competitors || 1;
  }, [paquetes, selectedPaquete]);

  // ==========================================
  // 4.5 EVALUACIÓN DE CONDICIONES
  // ==========================================
  const evaluateCondition = useCallback((condition) => {
    if (condition.type === 'addon' || (typeof condition.fieldId === 'string' && condition.fieldId.startsWith('addon_'))) {
      const pureId = condition.fieldId.replace('addon_', '');
      const cantidad = selectedAddons[pureId] || 0;
      return cantidad > 0;
    }

    if (condition.fieldId === 'packages' || condition.type === 'package') {
      return (
        selectedPaquete === condition.value ||
        (condition.options && condition.options.includes(selectedPaquete))
      );
    }

    const targetField = formConfig?.fields?.fields?.find((f) => f.id === parseInt(condition.fieldId));
    if (!targetField) {
      return false;
    }
    const fieldValue = String(targetField.value || '');
    const optionValues = (condition.options || []).map((opt) => {
      if (typeof opt === 'object' && opt !== null) {
        return String(opt.label || '');
      }
      return String(opt);
    });
    return optionValues.includes(fieldValue);
  }, [formConfig, selectedPaquete, selectedAddons]);

  // ==========================================
  // 🟢 NUEVA ARQUITECTURA: 4 FUNCIONES SEPARADAS
  // ==========================================

  // 1️⃣ EVALUACIÓN DE CONTEXTO (paquete + condiciones extra)
  const ruleMatchesContext = useCallback((rule) => {
    const rulePackageIds =
      rule.packageIds && rule.packageIds.length > 0
        ? rule.packageIds
        : rule.packageId
        ? [rule.packageId]
        : [];

    // FIX #4: packageIds vacío = NO aplica a ningún paquete
    const matchPackage =
      rulePackageIds.length > 0 &&
      rulePackageIds.map(String).includes(String(selectedPaquete));

    if (!matchPackage) return false;

    // Condiciones extra (ej: Tipo = Competitivo / Recreativo)
    if (rule.conditions && rule.conditions.length > 0) {
      const operator = rule.conditionOperator || 'OR';
      const matchExtra =
        operator === 'AND'
          ? rule.conditions.every(evaluateCondition)
          : rule.conditions.some(evaluateCondition);
      if (!matchExtra) return false;
    }

    return true;
  }, [selectedPaquete, evaluateCondition]);

  // 2️⃣ EVALUACIÓN DE EDAD (solo para reglas NO especiales)
  const ruleMatchesAge = useCallback((rule, computedAge) => {
    if (rule.isSpecial) return false; // Las especiales no se evalúan por edad
    if (computedAge === null || computedAge === undefined) return false;
    return computedAge >= Number(rule.minAge || 0) &&
           computedAge <= Number(rule.maxAge || 99);
  }, []);

  // 3️⃣ REGLA PARA AUTORRELLENO (contexto + edad, excluye especiales)
  const getAutoCategoryRule = useCallback((rules, computedAge) => {
    if (!rules || rules.length === 0) return null;
    if (computedAge === null || computedAge === undefined) return null;

    return rules.find((r) =>
      ruleMatchesContext(r) && ruleMatchesAge(r, computedAge)
    ) || null;
  }, [ruleMatchesContext, ruleMatchesAge]);

  // 4️⃣ REGLAS VISIBLES EN EL SELECT (según estado del checkbox)
  const getVisibleCategoryRules = useCallback((rules, computedAge, isUserSelected) => {
    if (!rules || rules.length === 0) return [];

    return rules.filter((r) => {
      if (!ruleMatchesContext(r)) return false;

      // Si el usuario marcó el checkbox → puede ver TODAS las reglas del contexto
      if (isUserSelected) return true;

      // Si no → solo ve las no-especiales que coinciden con su edad
      return ruleMatchesAge(r, computedAge);
    });
  }, [ruleMatchesContext, ruleMatchesAge]);

  // ==========================================
  // 5. EFECTOS
  // ==========================================
  // ✅ EFECTO 1: Sincronizar paquete
  useEffect(() => {
    if (selectedPackage && paquetes.length > 0) {
      const selectedId = String(selectedPackage.id_package || selectedPackage.id || '');
      const selectedTitle = selectedPackage.title || selectedPackage.name || '';
      
      const paqueteEncontrado = paquetes.find((p) => {
        const pkgId = String(p.id_package || p.id || '');
        const pkgTitle = p.title || p.name || '';
        return pkgId === selectedId || pkgTitle === selectedTitle;
      });
      
      if (paqueteEncontrado) {
        // 🔥 Establecer el valor usando la propiedad que realmente exista
        const valorASetear = paqueteEncontrado.id_package || paqueteEncontrado.id || paqueteEncontrado.title;
        
        setSelectedPaquete(valorASetear);
      }
    }
  }, [selectedPackage, paquetes, setSelectedPaquete]);

  // ✅ EFECTO 2: Sincronizar Addons
  useEffect(() => {
    if (!selectedPackage) return;
    const addonsData =
      selectedPackage.selectedAddons || selectedPackage.addonsElegidos;
    if (addonsData && Object.keys(addonsData).length > 0) {
      const addonsMap = {};
      Object.entries(addonsData).forEach(([addonId, value]) => {
        addonsMap[addonId] =
          typeof value === 'boolean' ? (value ? 1 : 0) : Number(value) || 0;
      });
      setSelectedAddons(addonsMap);
    } else if (paquetes.length > 0) {
      const selectedId = String(
        selectedPackage.id || selectedPackage.id_package || ''
      );
      const paqueteEncontrado = paquetes.find(
        (p) =>
          String(p.id_package || p.id || '') === selectedId ||
          p.title === selectedPackage.title
      );
      if (paqueteEncontrado?.addons) {
        const initialAddons = {};
        paqueteEncontrado.addons.forEach((addon) => {
          initialAddons[addon.id] = 0;
        });
        setSelectedAddons(initialAddons);
      }
    }
  }, [selectedPackage, paquetes]);

  // ✅ EFECTO 3: Autorrelleno de Edad y Categoría
  useEffect(() => {
    if (isEditable || !formConfig?.fields?.fields) return;
    const fields = formConfig.fields.fields;

    // 🔍 Buscar campo de fecha REAL (no por label "edad")
    const dateField = fields.find((f) => {
      const esFechaPorTipo = f.type === 'date';
      const esFechaPorLabel =
        f.label?.toLowerCase().includes('fecha') ||
        f.label?.toLowerCase().includes('nacimiento') ||
        f.label?.toLowerCase().includes('cumpleaños');
      const esFechaPorDataType = f.dataType === 'date';
      return esFechaPorTipo || esFechaPorLabel || esFechaPorDataType;
    });

    if (!dateField) return;
    const currentDate = dateField.value;
    if (!currentDate || currentDate.trim() === '') {
      lastProcessedDate.current = null;
      lastProcessedPackage.current = null;
      lastProcessedRulesSignature.current = null;
      return;
    }

    const categoryFieldsRules = fields
      .filter((f) => f.type === 'category')
      .map((f) => f.categoryRules || []);
    const currentRulesSignature = JSON.stringify(categoryFieldsRules);

    if (
      lastProcessedDate.current === currentDate &&
      lastProcessedPackage.current === selectedPaquete &&
      lastProcessedRulesSignature.current === currentRulesSignature
    ) {
      return;
    }

    const computedAge = calculateAge(currentDate);
    if (computedAge === null) return;

    lastProcessedDate.current = currentDate;
    lastProcessedPackage.current = selectedPaquete;
    lastProcessedRulesSignature.current = currentRulesSignature;

    let updated = false;
    const newFields = fields.map((field) => {
      const labelLower = field.label?.toLowerCase() || '';

      // 1. Autorrellenar Edad
      if (
        (labelLower === 'edad' || labelLower.includes('edad')) &&
        !field.isUserSelected
      ) {
        const currentValue = field.value ? String(field.value) : '';
        const newValue = String(computedAge);
        if (currentValue !== newValue) {
          updated = true;
          return { ...field, value: newValue };
        }
      }

      // 2. Autorrellenar Categoría — 🟢 usando getAutoCategoryRule
      if (
        field.type === 'category' ||
        labelLower.includes('categoría') ||
        labelLower.includes('categoria')
      ) {
        if (!field.isUserSelected && field.categoryRules?.length > 0) {
          const rule = getAutoCategoryRule(field.categoryRules, computedAge);
          const matchedCategory = rule ? rule.label : ''; // 🔥 vacío, no 'Sin Categoría'
          if (field.value !== matchedCategory) {
            updated = true;
            return { ...field, value: matchedCategory };
          }
        }
      }
      return field;
    });

    if (updated) {
      setFormConfig((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          fields: newFields,
        },
      }));
    }
  }, [
    formConfig?.fields?.fields,
    selectedPaquete,
    isEditable,
    setFormConfig,
    calculateAge,
    getAutoCategoryRule,
  ]);

  // ==========================================
  // 6. HANDLERS
  // ==========================================
  const handlePaqueteChange = useCallback(
    (e) => {
      const selectedId = e.target.value;
      setSelectedPaquete(selectedId);
      
      const selected = paquetes.find((p) => 
        String(p.id_package) === String(selectedId) || 
        String(p.id) === String(selectedId)
      );

      if (selected) {
        setTitle(selected.title || selected.name || '');
        setAmount(selected.amount || '');
        const numComp = parseInt(selected.number_of_competitors) || 1;
        const initialAddons = {};
        if (selected.addons && selected.addons.length > 0) {
          selected.addons.forEach((addon) => {
            initialAddons[addon.id] = numComp;
          });
        }
        setSelectedAddons(initialAddons);
      } else {
        setSelectedAddons({});
      }
    },
    [paquetes, setSelectedPaquete, setTitle, setAmount]
  );

  // ==========================================
  // 7. LÓGICA DE CONDICIONES Y VALIDACIÓN
  // ==========================================
  const shouldShowOption = useCallback((option) => {
    if (typeof option === 'string') return true;
    const { conditions, conditionOperator } = option;
    if (!conditions || conditions.length === 0) return true;
    const operator = conditionOperator || 'OR';
    const resultado = operator === 'AND'
      ? conditions.every(evaluateCondition)
      : conditions.some(evaluateCondition);
    return resultado;
  }, [evaluateCondition]);

  const shouldRenderField = useCallback((field) => {
    if (isEditable) return !field.isPackageInclude;
    if (field.isPackageInclude) return false;
    if (!field.fieldConditions || !Array.isArray(field.fieldConditions) || field.fieldConditions.length === 0) {
      return true;
    }
    const operator = field.conditionOperator || 'OR';
    const resultado = operator === 'AND'
      ? field.fieldConditions.every(evaluateCondition)
      : field.fieldConditions.some(evaluateCondition);
    return resultado;
  }, [isEditable, evaluateCondition]);

  const validateForm = useCallback(() => {
    if (!formConfig?.fields?.fields || !isFillingForm) return true;
    if (!aceptaDeslinde) {
      alert('Debe aceptar el deslinde de responsabilidades para continuar.');
      return false;
    }
    const mensajesErrores = [];
    const updatedFields = formConfig.fields.fields.map((field) => {
      if (deletedFields.includes(field.id) || field.isPackageInclude)
        return field;
      if (shouldRenderField(field)) {
        const fieldErrors = validators.validateFieldExtended(
          field,
          field.value
        );
        if (fieldErrors.length > 0) {
          mensajesErrores.push(`"${field.label}": ${fieldErrors[0]}`);
          return { ...field, invalid: true, errorMessage: fieldErrors[0] };
        } else {
          return { ...field, invalid: false, errorMessage: '' };
        }
      }
      return field;
    });

    if (mensajesErrores.length > 0) {
      setFormConfig((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          fields: updatedFields,
        },
      }));
      alert(
        `Por favor, corrija los siguientes errores:\n\n${mensajesErrores.join('\n')}`
      );
      return false;
    }
    return true;
  }, [
    formConfig,
    shouldRenderField,
    isFillingForm,
    aceptaDeslinde,
    deletedFields,
    setFormConfig,
  ]);

  const isFormValid = useCallback(() => {
    if (!formConfig?.fields?.fields || !aceptaDeslinde) return false;
    const camposVisibles = formConfig.fields.fields.filter(
      (f) =>
        !f.isPackageInclude &&
        !deletedFields.includes(f.id) &&
        shouldRenderField(f)
    );
    const camposInvalidos = camposVisibles.filter((field) => {
      if (field.required && (!field.value || String(field.value).trim() === '')) {
        return true;
      }
      if (
        field.dataType === 'integer' &&
        field.value &&
        !/^\d+$/.test(String(field.value))
      ) {
        return true;
      }
      if (
        field.dataType === 'email' &&
        field.value &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(field.value))
      ) {
        return true;
      }
      return false;
    });
    return camposInvalidos.length === 0;
  }, [formConfig, shouldRenderField, aceptaDeslinde, deletedFields]);

  const calculateResumen = useCallback(() => {
    if (!formConfig?.fields?.fields) return null;
    const camposActivos = formConfig.fields.fields
      .filter((field) => !deletedFields.includes(field.id) && shouldRenderField(field))
      .map((field) => ({
        nombre: field.label || 'Campo sin título',
        valor: field.value ?? 'Sin valor',
      }));

    const paqueteBase = paquetes.find((p) => 
      String(p.id_package) === String(selectedPaquete) || 
      String(p.id) === String(selectedPaquete)
    );
    const addonsFiltrados = (paqueteBase?.addons || [])
      .filter((addon) => Number(selectedAddons[addon.id] || 0) > 0)
      .map((addon) => ({
        ...addon,
        cantidad: Number(selectedAddons[addon.id]),
        extra_amount: addon.extra_amount || addon.precio || 0,
      }));

    const paqueteSeleccionado = paqueteBase
      ? {
          ...paqueteBase,
          addons: addonsFiltrados,
        }
      : {
          title: 'Ninguno',
          amount: '0',
          currency: 'Bs.',
          addons: [],
        };

    return {
      slug,
      paqueteSeleccionado,
      titulo: paqueteSeleccionado.title,
      monto: totalAmount,
      montoPorPersona: paqueteSeleccionado.amount || '0',
      numeroCompetidores: paqueteSeleccionado.number_of_competitors || 1,
      moneda: paqueteSeleccionado.currency || 'Bs.',
      camposActivos,
      datosFormulario: camposActivos,
    };
  }, [
    formConfig,
    paquetes,
    selectedPaquete,
    selectedAddons,
    deletedFields,
    shouldRenderField,
    slug,
    totalAmount,
  ]);

  // ==========================================
  // 8. HANDLERS DE MODALES
  // ==========================================
  const handleOpenModal = useCallback(() => {
    if (!validateForm()) return;
    const resumen = calculateResumen();
    if (resumen) {
      setResumenModal(resumen);
      setModalOpen(true);
    }
  }, [validateForm, calculateResumen]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setActiveModal(null);
    setSelectedField(null);
  }, []);

  const handleButtonClick = useCallback((field) => {
    setSelectedField(field.id);
    setExistingConditions(field.fieldConditions || []);
    setActiveModal('condicional');
  }, []);

  const handleSaveConditions = useCallback(
    (payload) => {
      const newConditions = Array.isArray(payload)
        ? payload
        : payload.conditions || [];
      const operator = Array.isArray(payload) ? 'OR' : payload.operator || 'OR';
      setFormConfig((prev) => ({
        ...prev,
        fields: {
          ...prev.fields,
          fields: prev.fields.fields.map((f) =>
            f.id === selectedField
              ? {
                  ...f,
                  fieldConditions: newConditions,
                  conditionOperator: operator,
                }
              : f
          ),
        },
      }));
      setConditions(newConditions);
      setActiveModal(null);
    },
    [selectedField, setFormConfig]
  );

  // ==========================================
  // 10. PAQUETES FILTRADOS
  // ==========================================
  const paquetesDisponibles = useMemo(() => {
    if (!paquetes || paquetes.length === 0) return [];
    return paquetes.filter((pkg) => !pkg.is_closed);
  }, [paquetes]);

  const paquetesCerrados = useMemo(() => {
    if (!paquetes || paquetes.length === 0) return [];
    return paquetes.filter((pkg) => pkg.is_closed);
  }, [paquetes]);

  // ==========================================
  // 9. RETORNO COMPLETO
  // ==========================================
  return {
    formConfig,
    setFormConfig,
    paquetes: paquetesDisponibles,
    paquetesCerrados,
    selectedPaquete,
    setSelectedPaquete,
    selectedAddons,
    setSelectedAddons,
    aceptaDeslinde,
    setAceptaDeslinde,
    deletedFields,
    setDeletedFields,
    fetchError,
    saveError,
    isLoading,
    resumenModal,
    modalOpen,
    activeModal,
    selectedField,
    existingConditions,
    conditions,
    optionConditionModal,
    submitError,
    title,
    amount,
    totalAmount,
    amountPerPerson,
    numberOfCompetitors,
    setModalOpen,
    setActiveModal,
    setSelectedField,
    setExistingConditions,
    setConditions,
    setOptionConditionModal,
    setSubmitError,
    shouldShowOption,
    shouldRenderField,
    validateForm,
    isFormValid,
    calculateResumen,
    handlePaqueteChange,
    handleOpenModal,
    handleCloseModal,
    handleButtonClick,
    handleSaveConditions,
    handleSave,
    handleTypeChangeWrapper,
    handleFieldChangeWrapper,
    handleLabelChangeWrapper,
    handleAddFieldWrapper,
    handleDeleteFieldWrapper,
    handleAddOptionWrapper,
    handleDeleteOptionWrapper,
    handleOptionChangeWrapper,
    handleOptionConditionsWrapper,
    handleAddCategoryRuleWrapper,
    handleUpdateCategoryRuleWrapper,
    handleDeleteCategoryRuleWrapper,
    handleToggleCategoryRulePackageWrapper,
    handleUpdateCategoryRuleConditionsWrapper,
    handleRequiredToggleWrapper,
    // 🟢 NUEVO: exponer funciones para PCFormulario
    calculateAge,
    getAutoCategoryRule,
    getVisibleCategoryRules,
    evaluateCondition,
  };
};

export default useFormulario;